import {useQuery} from '@tanstack/react-query';
import {Alert, Box, CircularProgress} from '@mui/material';
import apiCall from '../../../../utils/api';
import Indicator from '../../components/Indicator';
import Legend from '../../components/Legend';
import {IDashboardData, IFdcDashboardResult, SurveySlug} from '../../utils/types';
import {FDC_LABELS} from '../labels';

interface Props {
    container: string;
    uoaId: string;
    surveyId: string;
    surveyEvalId: string;
}

const buildApiKey = (slug: SurveySlug): string => (slug.denominator ? `${slug.numerator.question}_${slug.denominator.question}` : slug.numerator.question);

const buildSlugs = (surveyId: number, surveyEvalId: number): SurveySlug[] => [
    {
        numerator: {survey_id: surveyId, question: 'question9'},
        denominator: {survey_id: surveyId, question: 'question8'},
        operation: 'sum',
        key: 'i6',
        roman: 'VI',
        title: 'Tasso di rinnovamento degli enti richiedenti a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero di enti richiedenti per la prima volta e il numero complessivo di enti richiedenti nel corso dell'anno.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question8'},
        denominator: {survey_id: surveyEvalId, question: 'question2'},
        operation: 'sum',
        key: 'i7',
        roman: 'VII',
        title: 'Penetrazione degli enti ammissibili a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero complessivo di enti richiedenti durante l'anno e il numero complessivo di enti ammissibili del territorio.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question10'},
        denominator: {survey_id: surveyId, question: 'question11'},
        operation: 'sum',
        key: 'i8',
        roman: 'VIII',
        title: 'Tasso di approvazione delle richieste a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero di contributi deliberati durante l'anno e il numero complessivo di richieste di contributo ricevute nel corso dell'anno.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question11'},
        denominator: {survey_id: surveyId, question: 'question8'},
        operation: 'sum',
        key: 'i9',
        roman: 'IX',
        title: 'Numero medio di richieste per ente a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero complessivo di richieste di contributo ricevute nell'anno e il numero complessivo di enti richiedenti nel corso dell'anno.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question12'},
        denominator: {survey_id: surveyEvalId, question: 'question1'},
        operation: 'sum',
        key: 'i10',
        roman: 'X',
        title: 'Valore dei contributi per abitante adulto a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il valore complessivo dei contributi deliberati durante l'anno e il numero di abitanti del territorio di riferimento.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question13'},
        denominator: {survey_id: surveyId, question: 'question14'},
        operation: 'sum',
        key: 'i11',
        roman: 'XI',
        title: 'Tasso di rinnovamento degli enti sostenuti a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero di enti sostenuti per la prima volta nel corso dell'anno e il numero complessivo di enti sostenuti nel corso dell'anno.",
    },
    {
        numerator: {survey_id: surveyId, question: 'question10'},
        denominator: {survey_id: surveyId, question: 'question15'},
        operation: 'sum',
        key: 'i12',
        roman: 'XII',
        title: 'Intensità del finanziamento dei ricorrenti a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero complessivo di contributi deliberati durante l'anno e il numero complessivo di enti a cui è stato deliberato almeno un contributo negli ultimi 3 anni.",
    },
];

const Dashboard2 = ({container, uoaId, surveyId, surveyEvalId}: Props) => {
    const slugs = buildSlugs(Number(surveyId), Number(surveyEvalId));

    const {data, isLoading, isError} = useQuery<IFdcDashboardResult>({
        queryKey: ['fdc-dashboard2', container, uoaId, surveyId, surveyEvalId],
        queryFn: async () => {
            const res = await apiCall({
                url: `containers/${container}/uoa/${uoaId}/dashboards/fsv`,
                method: 'POST',
                data: {
                    with_groups: 1,
                    question_slugs: slugs.map((s) => ({
                        numerator: s.numerator,
                        denominator: s.denominator,
                        operation: s.operation,
                    })),
                },
            });

            const groupNumber: number | null = res.data['__group_number'] ?? null;
            const indicators: IDashboardData = {};
            for (const slug of slugs) {
                const apiKey = buildApiKey(slug);
                indicators[slug.key] = res.data[apiKey] ?? {uoa: null, average: null, group: null};
            }
            return {indicators, groupNumber};
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    return (
        <Box>
            <Legend labels={FDC_LABELS} groupNumber={data.groupNumber} />

            {slugs.map((slug) => (
                <Indicator
                    key={slug.key}
                    roman={slug.roman}
                    title={slug.title}
                    tooltip={slug.tooltip}
                    data={data.indicators[slug.key]}
                    isCount={true}
                    labels={FDC_LABELS}
                />
            ))}
        </Box>
    );
};

export default Dashboard2;
